/* eslint-disable jsx-a11y/click-events-have-key-events */
import axios from 'axios';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cl from 'classnames';
import polyline from '@mapbox/polyline';
import Datetime from 'react-datetime';
import Demo from './SkeletonDemo';
import Input from '../Input/Input';
import Constants from '../../Constants';
import LocalitiesAutocomplete from '../Woosmap/LocalitiesAutocomplete';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import DrivingImg from '../../images/car.png';
import CyclingImg from '../../images/cycle.png';
import WalkingImg from '../../images/walk.png';
import { tr } from '../utils/locale';
import markerIcon from '../../images/marker.png';
import { createWoosmapMap } from '../Map/drawOnMap';

const languages = [
    { value: 'fr', label: 'French' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'nl', label: 'Dutch' },
];

const travelModes = [
    { value: 'driving', label: 'Driving', img: DrivingImg },
    { value: 'cycling', label: 'Cycling', img: CyclingImg },
    { value: 'walking', label: 'Walking', img: WalkingImg },
];

const units = [
    { value: 'metric', label: 'Metric' },
    { value: 'imperial', label: 'Imperial' },
];

const methods = [
    { value: 'distance', label: 'Shortest' },
    { value: 'time', label: 'Fastest' },
];

export default class DistanceDemo extends Component {
    constructor(props) {
        super(props);
        const lang = (typeof window === 'object' && (navigator.language || navigator.userLanguage)) || 'gb';
        const defaultLang = languages.find((item) => lang.startsWith(item.value));

        this.defaultOrigin = {
            public_id: 'eastham',
            description: 'Eastham Village, Merseyide, United Kingdom',
            location: { lat: 53.31446402615734, lng: -2.9599091366015955 },
        };
        this.defaultDestination = {
            public_id: 'liverpool',
            description: 'Liverpool John Lennon Airport, United Kingdom',
            location: { lat: 53.333599, lng: -2.84972 },
        };

        this.state = {
            routes: [],
            error: null,
            originLocation: this.defaultOrigin.location,
            destinationLocation: this.defaultDestination.location,
            departureTime: null,
            arrivalTime: null,
            unit: units[0],
            method: methods[0],
            travelMode: travelModes[0],
            language: (defaultLang || languages[1]).value,
            alternatives: false,
        };

        this.mounted = true;
        this.map = null;
        this.markers = [];
        this.requestUrl = 'https://api.woosmap.com/distance/route/json';
        this.polylines = [];
        this.directionsRenderer = null;
        this.demoRef = React.createRef();
    }

    componentDidMount() {
        this.displayMap();
    }

    componentWillUnmount() {
        this.mounted = false;
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }

    displayOnMap = () => {
        if (this.map) {
            const { originLocation, destinationLocation, routes } = this.state;

            if (!this.directionsRenderer) {
                this.directionsRenderer = new window.woosmap.map.DirectionsRenderer({
                    preserveViewport: true,
                });
                this.directionsRenderer.setMap(this.map);
            }

            if (this.markers.length > 0) {
                this.markers.forEach((marker) => marker.setMap(null));
                this.markers = [];
            }

            const bounds = new window.woosmap.map.LatLngBounds();

            this.markers.push(
                new window.woosmap.map.Marker({
                    icon: { url: markerIcon, scaledSize: { height: 46, width: 30 } },
                    position: originLocation,
                    map: this.map,
                })
            );
            bounds.extend(new window.woosmap.map.LatLng(originLocation.lat, originLocation.lng));

            this.markers.push(
                new window.woosmap.map.Marker({
                    icon: { url: markerIcon, scaledSize: { height: 46, width: 30 } },
                    position: destinationLocation,
                    map: this.map,
                })
            );
            bounds.extend(new window.woosmap.map.LatLng(destinationLocation.lat, destinationLocation.lng));

            routes.forEach((route) =>
                Object.values(route.bounds).forEach((bound) =>
                    bounds.extend(new window.woosmap.map.LatLng(bound.lat, bound.lng))
                )
            );

            const decodedRoutes = routes.map((route) => ({
                ...route,
                overview_path: route.overview_polyline
                    ? polyline
                          .decode(route.overview_polyline.points)
                          .map((e) => new window.woosmap.map.LatLng(e[0], e[1]))
                    : undefined,
            }));

            this.directionsRenderer.setDirections({ routes: decodedRoutes });
            this.map.fitBounds(bounds, { top: 70, bottom: 70, left: 70, right: 70 });
        }
    };

    requestDistance = () => {
        const instance = axios.create();

        const processSuccess = (routes) => {
            if (this.mounted) this.setState({ routes, error: null }, () => this.displayOnMap());
        };

        const processError = (errorMessage) => {
            const message = errorMessage || 'Unhandled error';

            if (this.mounted) this.setState({ error: { message } });
        };

        instance
            .get(this.requestUrl, { params: this.getRequestparams() })
            .then((response) => {
                const isOk = response?.data?.status === 'OK' && response?.data?.routes;

                if (!isOk) processError(response?.data?.error_message);
                else processSuccess(response.data.routes);
            })
            .catch((error) => {
                processError(error?.data?.error_message);
            });
    };

    getRequestparams = () => {
        const {
            language,
            originLocation,
            destinationLocation,
            unit,
            method,
            travelMode,
            alternatives,
            arrivalTime,
            departureTime,
        } = this.state;

        const params = {
            key: Constants.woosmapKey,
            origin: `${originLocation.lat},${originLocation.lng}`,
            destination: `${destinationLocation.lat},${destinationLocation.lng}`,
            language,
            units: unit.value,
            method: travelMode.value === 'driving' ? method.value : 'distance',
            mode: travelMode.value,
            alternatives,
        };
        if (departureTime) {
            params.departure_time = departureTime.unix();
        } else if (arrivalTime) {
            params.arrival_time = arrivalTime.unix();
        }
        return params;
    };

    renderRoutesSummary = () => {
        const { routes } = this.state;

        if (!routes || routes.length === 0) {
            return <div className="demo__empty">{tr('No route found')}</div>;
        }

        const result = routes.map((route, key) => (
            <li key={route.summary}>
                <div className="demo__results__item">
                    <strong>
                        {routes.length > 1 ? `${key + 1}.` : ''} {route.summary}
                    </strong>
                </div>
                <div className="demo__results__item">
                    {' '}
                    - distance: <strong>{route.legs[0].distance.text}</strong>
                </div>
                <div className="demo__results__item">
                    {' '}
                    - duration: <strong>{route.legs[0].duration.text}</strong>
                </div>
            </li>
        ));

        return <ul>{result}</ul>;
    };

    displayMap = () => {
        if (this.timeoutMap) {
            clearTimeout(this.timeoutMap);
        }

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.woosmapKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
            this.requestDistance();
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    localitiesOnChangeCallBack = (type) => (item) => {
        if (!item) {
            return;
        }
        const newState = {};
        newState[`${type}Location`] = item ? item.location : {};
        this.setState(newState, this.requestDistance);
    };

    renderHeaderFilters = () => {
        const { language } = this.state;
        const originInputs = {
            label: tr('Origin'),
            component: (
                <>
                    <LocalitiesAutocomplete
                        woosmapKey={Constants.woosmapKey}
                        defaultLocality={this.defaultOrigin}
                        language={language.value}
                        types="locality|postal_code|airport"
                        callback={this.localitiesOnChangeCallBack('origin')}
                    />
                </>
            ),
        };

        const destinationInputs = {
            label: tr('Destination'),
            component: (
                <>
                    <LocalitiesAutocomplete
                        woosmapKey={Constants.woosmapKey}
                        defaultLocality={this.defaultDestination}
                        language={language.value}
                        types="locality|postal_code|airport"
                        callback={this.localitiesOnChangeCallBack('destination')}
                    />
                </>
            ),
        };

        const departureTimeInput = {
            label: tr('Departure Time'),
            component: (
                <>
                    <Datetime
                        key="dt_departure"
                        className="datetime"
                        inputProps={{ className: 'input__item' }}
                        onChange={(dt) => this.setState({ departureTime: dt, arrivalTime: null }, this.requestDistance)}
                    />
                </>
            ),
        };

        const arrivalTimeInput = {
            label: tr('Arrival Time'),
            component: (
                <>
                    <Datetime
                        key="dt_arrival"
                        className="datetime"
                        inputProps={{ className: 'input__item' }}
                        onChange={(dt) => this.setState({ arrivalTime: dt, departureTime: null }, this.requestDistance)}
                    />
                </>
            ),
        };

        return [originInputs, destinationInputs, departureTimeInput, arrivalTimeInput];
    };

    renderFooterFilters = () => {
        const { unit, method, travelMode, language, alternatives } = this.state;

        const filterTravelMode = {
            key: 'travelmode',
            component: (
                <ButtonGroup className="transportation" isLight>
                    {travelModes.map((item) => (
                        <Button
                            type="group"
                            size="small"
                            isBtnIcon
                            key={item.value}
                            active={item === travelMode}
                            onClick={() => this.setState({ travelMode: item }, this.requestDistance)}
                        >
                            <img alt={item.label} src={item.img} />
                        </Button>
                    ))}
                </ButtonGroup>
            ),
        };

        const filterUnit = {
            key: 'unit',
            component: (
                <ButtonGroup isLight>
                    {units.map((item) => (
                        <Button
                            type="group"
                            size="small"
                            key={item.value}
                            label={item.label}
                            active={item === unit}
                            onClick={() => this.setState({ unit: item }, this.requestDistance)}
                        />
                    ))}
                </ButtonGroup>
            ),
        };

        const filterMethod = {
            key: 'method',
            component: (
                <ButtonGroup isLight>
                    {methods.map((item) => (
                        <Button
                            type="group"
                            size="small"
                            key={item.value}
                            label={item.label}
                            active={item === method}
                            onClick={() => this.setState({ method: item }, this.requestDistance)}
                        />
                    ))}
                </ButtonGroup>
            ),
        };

        const filterLanguage = {
            key: 'language',
            label: tr('Language'),
            component: (
                <ButtonGroup className="language" isLight>
                    {languages.map((item) => (
                        <Button
                            type="group"
                            size="small"
                            key={item.value}
                            active={language === item.value}
                            onClick={() => {
                                this.setState({ language: item.value }, this.requestDistance);
                            }}
                        >
                            <span className={cl('flag', `flag-${item.value}`)} title={item.label} />
                        </Button>
                    ))}
                </ButtonGroup>
            ),
        };

        const filterAlternatives = {
            key: 'alternatives',
            label: '',
            component: (
                <Input
                    label={tr('Alternative routes')}
                    type="checkbox"
                    checked={alternatives}
                    onChange={(e) => {
                        this.setState({ alternatives: e.target.checked }, this.requestDistance);
                    }}
                />
            ),
        };

        return [filterAlternatives, filterTravelMode, filterMethod, filterLanguage, filterUnit];
    };

    render() {
        const { routes, error } = this.state;
        const { noheader } = this.props;
        const headerLabels = {
            title: tr('Distance API'),
            desc: tr(
                'The Woosmap Distance API delivers road distance and travel time between origins and destinations, to plan a simple journey (up to 25 waypoints) or for matrix computations (up to 200 elements).'
            ),
        };
        return (
            <Demo
                id="distance-demo"
                className="demo--distance"
                docLink="https://developers.woosmap.com/products/distance-api/get-started/"
                header={headerLabels}
                footerFilters={this.renderFooterFilters()}
                headerFilters={this.renderHeaderFilters()}
                request={this.requestUrl}
                noheader={noheader}
                params={this.getRequestparams()}
                referer="http://localhost/"
                response={error || routes}
                result={this.renderRoutesSummary()}
                withMap
                ref={this.demoRef}
            />
        );
    }
}

DistanceDemo.defaultProps = {
    noheader: false,
};
DistanceDemo.propTypes = {
    noheader: PropTypes.bool,
};
